import { getSensorIdsMap, getSensorIdsMapResolved } from '@Actions/sensors';
import {
	Button,
	EmptyTableBody,
	SensorIllustration,
	StylovyzeTableBody,
	StylovyzeTableHeaderProps,
	StylovyzeTableRow,
	useSettings,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { STATUS_ENUM } from '@Types/editSensorData.types';
import { SENSOR_GROUP_ROUTES } from '@Types/routes';
import {
	ListSensorSortBy,
	SensorGroupSort,
	SensorGroupsSearchProps,
} from '@Types/sensorGroup.types';
import { addRouteNamespace } from '@Utils/actions';
import formatDateTime from '@Utils/formatDateTime';
import { TSensorTypes } from '@Utils/valuemappingFormHelpers';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

interface SensorGroupsTableBodyProps {
	searchParams: SensorGroupsSearchProps;
	setSearchParams: React.Dispatch<
		React.SetStateAction<SensorGroupsSearchProps>
	>;
}

const getSortKey = (sort?: SensorGroupSort) => {
	if (sort) {
		const sortKeys = Object.keys(sort);
		if (sortKeys.length === 0) return null;
		return sortKeys[0];
	}
	return null;
};

const getSortOrder = (sort?: SensorGroupSort) => {
	if (sort) {
		const sortValues = Object.values(sort);
		if (sortValues.length === 0) return null;
		return sortValues[0];
	}
	return null;
};

export default function SensorGroupsTableBody({
	searchParams,
	setSearchParams,
}: SensorGroupsTableBodyProps) {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const { sensorGroups = [], status: sensorGroupsStatus } =
		useSelector(getValue).sensorGroup?.sensorGroups;
	const { sensorIdMap, status: sensorIdMapStatus } =
		useSelector(getValue).sensors?.sensorIdMap;

	const { companySettings } = useSettings();
	const { dateFormat, hourCycle12, timeZoneIANA } = companySettings;

	const format = `${dateFormat} - ${hourCycle12 ? 'hh' : 'HH'}:mm:ss ${
		hourCycle12 ? 'A' : ''
	}`;

	const headers: StylovyzeTableHeaderProps[] = [
		{
			key: 'name',
			cell: t('Group Name'),
			width: '120px',
			align: 'left',
			sortable: true,
		},
		{
			key: 'Sensor Type',
			cell: t('Sensor Type'),
			width: '120px',
			align: 'left',
		},
		{
			key: 'Sensors',
			cell: t('Sensors'),
			width: 'auto',
			maxWidth: '200px',
			align: 'left',
		},
		{
			key: 'lastModifiedDate',
			cell: t('Last Updated'),
			width: '150px',
			align: 'center',
			sortable: true,
		},
	];

	const [rows, setRows] = useState<StylovyzeTableRow[]>([]);

	useEffect(() => {
		if (sensorGroupsStatus === STATUS_ENUM.SUCCESS) {
			if (sensorGroups.length) {
				const sensorIdsSet = new Set<string>();
				sensorGroups.forEach(sensorGroup => {
					sensorGroup.sensorGroupPairs.forEach(sensorPair => {
						sensorIdsSet.add(sensorPair.sensorId);
					});
				});
				dispatch(
					getSensorIdsMap({
						sensorIds: encodeURIComponent(
							Array.from(sensorIdsSet).join(','),
						),
						limit: 1000,
						configType: 'sensor',
					}),
				);
			} else {
				dispatch(getSensorIdsMapResolved({}));
			}
		}
	}, [sensorGroups]);

	useEffect(() => {
		if (
			sensorIdMapStatus === STATUS_ENUM.SUCCESS ||
			sensorIdMapStatus === STATUS_ENUM.ERROR
		) {
			const translationTypeValues = TSensorTypes(t);
			setRows(
				sensorGroups?.map(sensorGroup => {
					const dateFormatted =
						formatDateTime(
							new Date(sensorGroup.lastModifiedDate),
							timeZoneIANA,
							format,
						) || [];

					return {
						key: sensorGroup._id,
						pathToGoWhenClicked: addRouteNamespace(
							`sensor-group/${sensorGroup._id}`,
						),
						cells: [
							sensorGroup.name,
							translationTypeValues[sensorGroup.sensorType] ||
								sensorGroup.sensorType,
							{
								content: sensorGroup.sensorGroupPairs
									?.map(i => {
										if (
											i.sensorId in sensorIdMap &&
											sensorIdMap[i.sensorId].sensorAlias
										) {
											return sensorIdMap[i.sensorId]
												.sensorAlias;
										}
										return i.sensorId;
									})
									.join(', '),
								ellipsis: true,
							},
							dateFormatted,
						],
					};
				}),
			);
		}
	}, [sensorIdMap]);

	return (
		<StylovyzeTableBody
			dataCy="sensorGroupTable"
			headers={headers}
			rows={rows}
			useSkeleton={{ rowCount: searchParams.limit || 10 }}
			initialized={
				sensorGroupsStatus === STATUS_ENUM.SUCCESS &&
				(sensorIdMapStatus === STATUS_ENUM.SUCCESS ||
					sensorIdMapStatus === STATUS_ENUM.ERROR)
			}
			sort={{
				name: getSortKey(searchParams?.sort) || 'lastModifiedDate',
				order: getSortOrder(searchParams?.sort) || 'desc',
				onRequestSort: (_e, name, order) => {
					setSearchParams(_searchParams => ({
						..._searchParams,
						sort: {
							[name as ListSensorSortBy]: order,
						},
					}));
				},
			}}
			emptyTableBody={
				<EmptyTableBody
					dataCy="sensor-group-empty-table-body"
					title={t('There are no Sensor Groups to display.')}
					subtitle={t(
						'A sensor group is a user-defined group of sensors that is collecting data of a specific sensor type.',
					)}
					icon={<SensorIllustration />}
					color={'#4A6067'}
					CTA={
						<Button
							variant="contained"
							color="primary"
							onClick={() => {
								history.push(
									addRouteNamespace(
										SENSOR_GROUP_ROUTES.create,
									),
								);
							}}>
							{t('Create Sensor Group')}
						</Button>
					}
				/>
			}
		/>
	);
}
